// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, formatUsd } from '@polarissleep/shared';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { JSX } from 'react';
import { Link } from 'react-router';
import type { PatientCharge } from '../hooks/usePatientCharges';
import { useUpcomingPayableAppointments } from '../hooks/useUpcomingPendingAppointments';

const BRAND = '#640040';
const CARD_DECLINED_URL = 'https://polarissleep.com/fhir/StructureDefinition/payment-card-declined-at';

export function PaymentBanner({ charges = [] }: { charges?: PatientCharge[] }): JSX.Element | null {
  const { appointments, loading } = useUpcomingPayableAppointments();
  const chargePayments = charges.filter(
    (charge) =>
      (charge.status === 'pending' || charge.status === 'overdue') &&
      charge.balanceDue > 0 &&
      (charge.selfPay ||
        !charge.charge.identifier?.some((identifier) => identifier.system === APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM))
  );
  if (loading || (appointments.length === 0 && chargePayments.length === 0)) {
    return null;
  }
  return (
    <Stack gap="xs" my="sm">
      {appointments.map((appt) => (
        <Alert
          key={appt.id}
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title="Payment required for upcoming appointment"
        >
          <Group justify="space-between" wrap="wrap" align="center">
            <Text size="sm">
              {appt.appointmentType?.text ?? 'Appointment'}
              {' · '}
              {appt.start ? new Date(appt.start).toLocaleString() : ''}
            </Text>
            <Link to={`/pay/${appt.id}`} style={{ color: BRAND, fontWeight: 600 }}>
              Pay Now →
            </Link>
          </Group>
        </Alert>
      ))}
      {chargePayments.length > 1 && (
        <Text size="sm" fw={600}>
          Payment due for {chargePayments.length} visits
        </Text>
      )}
      {chargePayments.map((charge) => (
        <Alert
          key={charge.id}
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title="Payment due"
          radius="md"
          styles={{
            root: {
              border: '1px solid var(--mantine-color-orange-3)',
              backgroundColor: 'var(--mantine-color-orange-0)',
            },
            title: { color: 'var(--mantine-color-orange-8)' },
            message: { width: '100%' },
          }}
        >
          <Group justify="space-between" wrap="wrap" align="center" gap="md">
            <Stack gap={2} style={{ flex: 1, minWidth: 180 }}>
              <Text fw={700} fz="xl">
                {formatUsd(charge.balanceDue)}
              </Text>
              <Text size="sm" c="dimmed">
                {charge.visitSummary ??
                  [charge.purpose, charge.charge.occurrenceDateTime?.slice(0, 10)].filter(Boolean).join(' · ')}
              </Text>
              {charge.status === 'overdue' && (
                <Text size="sm" c="red">
                  Overdue{charge.dueDateISO ? ` since ${new Date(charge.dueDateISO).toLocaleDateString()}` : ''}
                </Text>
              )}
              {charge.charge.extension?.some((extension) => extension.url === CARD_DECLINED_URL) && (
                <Text size="sm" c="red">
                  Card declined. Update your card to pay this charge.
                </Text>
              )}
            </Stack>
            <Button
              component={Link}
              to={`/pay-charge/${charge.id}`}
              size="md"
              style={{ backgroundColor: BRAND, flexShrink: 0 }}
            >
              Pay {formatUsd(charge.balanceDue)}
            </Button>
          </Group>
        </Alert>
      ))}
    </Stack>
  );
}
